/**
 * WebSocket Context Provider for Dashboard
 *
 * Provides WebSocket connection state and event handling across dashboard components.
 * Manages:
 * - WebSocket connection lifecycle (status, error, disconnect, reconnect)
 * - Incoming event processing with filter matching
 * - "New events available" counter for filtered-out events
 * - Highlighted event IDs with automatic 5-second cleanup
 * - Live map alert flashes, dismissed when an event is marked read
 */

import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { eventSeverityToNumber } from "@sitescene/shared";
import {
	type Event,
	EventSeverity,
	type EventWithSensors,
} from "@sitescene/types";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "@untitledui/icons";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useLocation } from "react-router";
import { toast } from "sonner";

import type {
	ConnectionStatus,
	ParsedDashboardEvent,
} from "@/hooks/useWebSocketConnection";

import { queryKeys } from "@/api/query-keys";
import { Button } from "@/components/ui/buttons/button";
import { ROUTES } from "@/constants/routes";
import {
	useAllActivityEventsState,
	useDashboardEventsState,
} from "@/context/event-filters-registry";
import { type EventsState } from "@/context/events";
import {
	extractMapEventFlashTarget,
	type MapEventFlashTarget,
} from "@/features/dashboard/map/map-event-flash";
import { useResolveTimeFilterZone } from "@/features/events/hooks/useTimeFilterZone";
import { captureEventWatchMatched } from "@/features/watchlist/utils/watch-analytics";
import { useLocationZoneSensorIdSets } from "@/hooks/useOrgLocationTree";
import { useWebSocketConnection } from "@/hooks/useWebSocketConnection";
import { cx } from "@/utils/cx";
import { buildDateTimeRangeTimestamps } from "@/utils/date-time";
import { mergeWebsocketEventIntoCachedEvent } from "@/utils/event-cache-merge";
import {
	doesEventMatchFilters,
	doesEventMatchFiltersFromCache,
} from "@/utils/event-filter-matcher";
import {
	isOpenOnlyEventsListQueryKey,
	removeEventFromInfiniteQueryData,
	shouldRemoveEventFromOpenOnlyLists,
} from "@/utils/event-list-cache";
import { updateLatestEventBySensorCaches } from "@/utils/latest-event-by-sensor-cache";
import { severityConfig } from "@/utils/severity";
import {
	convertWebSocketEventToEvent,
	type WebSocketEventDetail,
} from "@/utils/websocket-event-converter";

/**
 * Duration in milliseconds to keep events highlighted in the alert list.
 * 5.25 seconds to account for animation duration of .75s animating 5 times.
 */
const HIGHLIGHT_DURATION_MS = 5250;

/**
 * How long a newly arrived event pulses on the dashboard map cluster or pin.
 * Same duration as the alert-list highlight; kept as a separate constant so
 * either side can change independently.
 */
const MAP_FLASH_DURATION_MS = 5250;

const EVENTS_VISIBLE_PATHS = [ROUTES.DASHBOARD, ROUTES.ALL_ACTIVITY];

/**
 * Maximum number of events to keep in the cached/filtered-out Maps.
 * Prevents unbounded memory growth when a long-lived session receives many WS events.
 * Oldest entries (first inserted) are evicted when the limit is exceeded.
 */
const MAX_CACHED_EVENTS = 200;

/**
 * WebSocket context value
 */
export interface WebSocketContextValue {
	/** Clear the new events counter */
	clearNewEventsCount: () => void;
	/** Manually disconnect the WebSocket */
	disconnect: () => void;
	/** Drop a live alert from map cluster/pin chrome after it is viewed. */
	dismissMapEventFlash: (eventId: string) => void;
	/** Error message if connection failed */
	error: null | string;
	/** Set of event IDs that are currently highlighted */
	highlightedEventIds: Set<string>;
	/** Live alerts that arrived while the dashboard is open (map pulse, then standing count). */
	mapEventFlashes: MapEventFlashTarget[];
	/** Count of events that arrived but did not match current filters */
	newEventsCount: number;
	/** Manually trigger reconnection */
	reconnect: () => void;
	/** Current connection status */
	status: ConnectionStatus;
}

/**
 * Evict the oldest entries from a Map when it exceeds maxSize.
 * Maps iterate in insertion order, so the first keys are the oldest.
 */
function enforceMapSizeLimit<K, V>(map: Map<K, V>, maxSize: number): void {
	if (map.size <= maxSize) return;
	const iterator = map.keys();
	for (let i = 0, n = map.size - maxSize; i < n; i++) {
		const key = iterator.next().value;
		if (key !== undefined) map.delete(key);
	}
}

/**
 * Wait for in-flight event-list fetches to finish after a filter change.
 * Yields briefly so React Query can schedule the new-key fetch, then waits
 * if one is running. Does not force a refetch — that thrashes the live list
 * when this effect re-runs for non-filter reasons.
 */
async function waitForListQueriesToSettle(
	queryClient: QueryClient,
	queryKey: QueryKey,
): Promise<void> {
	// Yield so useInfiniteQuery can start the fetch for the new filter key.
	await Promise.resolve();
	await new Promise<void>((resolve) => {
		requestAnimationFrame(() => resolve());
	});

	// One more macrotask if the fetch still has not started (common right after
	// a batched reset + hydrate).
	if (queryClient.isFetching({ queryKey }) === 0) {
		await new Promise<void>((resolve) => {
			setTimeout(resolve, 0);
		});
	}

	if (queryClient.isFetching({ queryKey }) === 0) {
		return;
	}

	await new Promise<void>((resolve) => {
		const unsubscribe = queryClient.getQueryCache().subscribe(() => {
			if (queryClient.isFetching({ queryKey }) === 0) {
				unsubscribe();
				resolve();
			}
		});
	});
}

/**
 * WebSocket context
 */
const WebSocketContext = createContext<undefined | WebSocketContextValue>(
	undefined,
);

/**
 * Props for WebSocketProvider
 */
export interface WebSocketProviderProps {
	children: ReactNode;
	/** WebSocket URL to connect to */
	websocketUrl: string;
}

/**
 * Hook to access WebSocket context
 * @throws Error if used outside of WebSocketProvider
 */
// eslint-disable-next-line react-refresh/only-export-components -- Hook is intentionally co-located with its Context
export function useWebSocket(): WebSocketContextValue {
	const context = useContext(WebSocketContext);
	if (context === undefined) {
		throw new Error("useWebSocket must be used within a WebSocketProvider");
	}
	return context;
}

/**
 * WebSocket Provider Component
 *
 * Wraps dashboard components to provide WebSocket connection state and event handling.
 * Must be nested inside EventFiltersProvider to access filter state.
 *
 * If websocketUrl is empty, the provider will not attempt to connect and will provide
 * a no-op context that allows components to work without WebSocket functionality.
 */
export function WebSocketProvider({
	children,
	websocketUrl,
}: WebSocketProviderProps) {
	// Access React Query client for cache updates
	const queryClient = useQueryClient();

	const dashboardFiltersState = useDashboardEventsState();
	const allActivityFiltersState = useAllActivityEventsState();
	const location = useLocation();

	const activeFiltersState = useMemo(
		() =>
			location.pathname === ROUTES.ALL_ACTIVITY
				? allActivityFiltersState
				: dashboardFiltersState,
		[allActivityFiltersState, dashboardFiltersState, location.pathname],
	);

	const activeFilterProps = useMemo<EventsState>(
		() => ({
			dateRangeFilter: activeFiltersState.dateRangeFilter,
			eventTypesFilter: activeFiltersState.eventTypesFilter,
			isBulkEditing: false,
			locationsFilter: activeFiltersState.locationsFilter,
			searchQueryFilter: activeFiltersState.searchQueryFilter,
			selectedEventIds: new Set<string>(),
			sensorsFilter: activeFiltersState.sensorsFilter,
			severityFilter: activeFiltersState.severityFilter,
			statusFilter: activeFiltersState.statusFilter,
			zonesFilter: activeFiltersState.zonesFilter,
		}),
		[activeFiltersState],
	);

	// Same location-derived TZ used by the events list API — keep WS matching aligned.
	// useResolveTimeFilterZone (not useTimeFilterZone): WebSocketProvider is app-level
	// and sits outside EventFilterScopeProvider.
	const { timeZone: timeFilterZone } = useResolveTimeFilterZone(
		activeFiltersState.locationsFilter,
	);

	// Org-tree ID sets so "all locations/zones/sensors selected" is not treated as a
	// restrictive L/Z/S filter (mirrors URL/API "omit when all selected" behavior).
	// Memoize: useLocationZoneSensorIdSets returns a new object each render.
	const { locationIds, sensorIds, zoneIds } = useLocationZoneSensorIdSets();
	const idSets = useMemo(
		() => ({ locationIds, sensorIds, zoneIds }),
		[locationIds, sensorIds, zoneIds],
	);
	// Ref for the handleEvent callback to avoid stale closures
	const filtersRef = useRef(activeFilterProps);
	const timeFilterZoneRef = useRef(timeFilterZone);
	const idSetsRef = useRef(idSets);
	useLayoutEffect(() => {
		filtersRef.current = activeFilterProps;
		timeFilterZoneRef.current = timeFilterZone;
		idSetsRef.current = idSets;
	}, [activeFilterProps, idSets, timeFilterZone]);

	// Track the current pathname to detect when the user is away from event pages
	const pathnameRef = useRef(location.pathname);
	useLayoutEffect(() => {
		pathnameRef.current = location.pathname;
	}, [location.pathname]);

	// Track highlighted event IDs
	const [highlightedEventIds, setHighlightedEventIds] = useState<Set<string>>(
		new Set(),
	);
	const [mapEventFlashes, setMapEventFlashes] = useState<MapEventFlashTarget[]>(
		[],
	);

	// Track count of events not matching filters
	const [newEventsCount, setNewEventsCount] = useState(0);

	// Store websocket events that were filtered out, so we can add them back
	// when filters change to match them
	const filteredOutEventsRef = useRef<Map<string, ParsedDashboardEvent>>(
		new Map(),
	);

	// Store websocket events that were successfully added to the cache
	// so we can preserve them when filters change and queries refetch
	const cachedWebSocketEventsRef = useRef<Map<string, Event>>(new Map());

	// Refs for highlight timeout management to avoid stale closure issues
	const highlightTimeoutsRef = useRef<
		Map<
			string,
			{
				list?: ReturnType<typeof setTimeout>;
				map?: ReturnType<typeof setTimeout>;
			}
		>
	>(new Map());

	// Skip the first execution of the filter-change effect to prevent
	// a redundant invalidation on mount (the initial query fetch handles this).
	const hasInitializedRef = useRef(false);

	/**
	 * Highlight the event in the list for a few seconds. When `flashTarget` is
	 * provided (new live alerts only), also pulse the matching map cluster/pin;
	 * after the pulse, the event stays on the map so clusters can keep an
	 * "N Alerts" count for this session. List and map timing are independent.
	 * Passing no `flashTarget` (e.g. on `event.updated`) refreshes the list
	 * highlight only and leaves any existing map flash / pulse timer alone.
	 */
	const addHighlightedEvent = useCallback(
		(eventId: string, flashTarget?: MapEventFlashTarget) => {
			const existingTimeout = highlightTimeoutsRef.current.get(eventId);
			if (existingTimeout) {
				if (existingTimeout.list) clearTimeout(existingTimeout.list);
				// Only reset the map pulse when starting a new flash — updates
				// must not cancel an in-flight created pulse or standing count.
				if (flashTarget && existingTimeout.map) {
					clearTimeout(existingTimeout.map);
				}
			}

			setHighlightedEventIds((prev) => new Set(prev).add(eventId));
			if (flashTarget) {
				setMapEventFlashes((prev) => {
					const next = [
						...prev.filter((flash) => flash.eventId !== eventId),
						// Pulse first; after MAP_FLASH_DURATION_MS this becomes a standing count.
						{ ...flashTarget, isPulsing: true },
					];
					return next.length > MAX_CACHED_EVENTS
						? next.slice(-MAX_CACHED_EVENTS)
						: next;
				});
			}

			const list = setTimeout(() => {
				setHighlightedEventIds((prev) => {
					const next = new Set(prev);
					next.delete(eventId);
					return next;
				});
				const current = highlightTimeoutsRef.current.get(eventId);
				if (current && !current.map) {
					highlightTimeoutsRef.current.delete(eventId);
				} else if (current?.map) {
					highlightTimeoutsRef.current.set(eventId, { map: current.map });
				}
			}, HIGHLIGHT_DURATION_MS);

			const map = flashTarget
				? setTimeout(() => {
						setMapEventFlashes((prev) =>
							prev.map((flash) =>
								flash.eventId === eventId
									? { ...flash, isPulsing: false }
									: flash,
							),
						);
						const current = highlightTimeoutsRef.current.get(eventId);
						if (current && !current.list) {
							highlightTimeoutsRef.current.delete(eventId);
						} else if (current?.list) {
							highlightTimeoutsRef.current.set(eventId, {
								list: current.list,
							});
						}
					}, MAP_FLASH_DURATION_MS)
				: existingTimeout?.map;

			highlightTimeoutsRef.current.set(eventId, { list, map });
		},
		[],
	);

	/**
	 * Clear the new events counter
	 */
	const clearNewEventsCount = useCallback(() => {
		setNewEventsCount(0);
	}, []);

	/**
	 * Stop counting a live alert on map clusters/pins once the operator views it.
	 * List highlight timing is independent and is left alone.
	 */
	const dismissMapEventFlash = useCallback((eventId: string) => {
		setMapEventFlashes((prev) =>
			prev.filter((flash) => flash.eventId !== eventId),
		);

		const timeouts = highlightTimeoutsRef.current.get(eventId);
		if (!timeouts?.map) return;

		clearTimeout(timeouts.map);
		if (timeouts.list) {
			highlightTimeoutsRef.current.set(eventId, { list: timeouts.list });
		} else {
			highlightTimeoutsRef.current.delete(eventId);
		}
	}, []);

	/**
	 * Handle incoming WebSocket events with filter matching and React Query cache updates
	 *
	 * - On event.created: if matches filters, invalidate list queries to trigger refetch; otherwise increment counter
	 * - On event.updated: if matches filters, update existing events in React Query cache; otherwise increment counter
	 */
	const handleEvent = useCallback(
		(event: ParsedDashboardEvent) => {
			const eventId = getEventIdFromParsedEvent(event);
			const eventDetail = event.event.detail;
			const eventSeverity = eventDetail.severity;
			const websocketEventType = event.type;
			const addedWatches = eventDetail.addedWatches ?? [];

			if (addedWatches.length > 0 && eventId) {
				captureEventWatchMatched({
					addedWatchIds: addedWatches,
					eventId,
					updateType:
						"updateType" in eventDetail ? eventDetail.updateType : undefined,
					watchCount: eventDetail.watches?.length,
					websocketEventType,
				});
			}
			const moderateEventSeverity = eventSeverityToNumber(
				EventSeverity.Moderate,
			);

			const isOnEventsPage = EVENTS_VISIBLE_PATHS.some((path) =>
				pathnameRef.current.startsWith(path),
			);

			// Match once against the user's filters (regardless of page) — used to decide
			// whether to show a toast and increment the counter. On an events page the
			// same result also decides list visibility; elsewhere every event enters the
			// cache so it is treated as matching.
			const matchesUserFilters = doesEventMatchFilters(
				eventDetail,
				filtersRef.current,
				timeFilterZoneRef.current,
				undefined,
				idSetsRef.current,
			);
			const matchesFilters = isOnEventsPage ? matchesUserFilters : true;

			if (import.meta.env.DEV) {
				console.log("[WebSocket] Event received:", {
					eventId,
					filters: filtersRef.current,
					matchesFilters,
					type: websocketEventType,
				});
			}

			// Show toast unless the user can already see the event appear in the list
			// (i.e., they're on an events page AND the event matches active filters).
			const eventVisibleInList = isOnEventsPage && matchesUserFilters;
			const isEventSeverityModerateOrHigher =
				eventSeverity >= moderateEventSeverity;

			if (
				websocketEventType === "event.created" &&
				!eventVisibleInList &&
				isEventSeverityModerateOrHigher
			) {
				const severity = severityConfig(eventSeverity);
				const priorityName = severity.badgeText;
				const priorityToastClassName = severity.toastClassName;
				const locationName = getLocationNameFromDetail(eventDetail);
				const toastTitle = locationName
					? `${priorityName} at ${locationName}`
					: priorityName;

				toast.custom(
					(t) => (
						<div
							className={cx(
								priorityToastClassName,
								"flex w-full max-w-md min-w-sm items-start rounded-xl py-4 pr-8 pl-4 text-white shadow-lg ring",
							)}
							role="alert"
						>
							<div className="flex w-full flex-col items-start gap-2">
								<h5 className="w-full truncate text-sm font-semibold">
									{toastTitle}
								</h5>
								<div className="w-full truncate text-sm">
									{eventDetail.title}
								</div>
								<Button
									color="secondary"
									href={`${ROUTES.EVENTS}/${eventId}`}
									size="sm"
								>
									View Alert
								</Button>
							</div>
							<button
								aria-label="Dismiss notification"
								className="absolute top-0 right-0 cursor-pointer p-2"
								onClick={() => toast.dismiss(t)}
							>
								<X className="size-6" />
							</button>
						</div>
					),
					{
						duration: 10000,
					},
				);
			}

			// Convert WebSocket event to Event object for cache updates
			const eventData = convertWebSocketEventToEvent(eventDetail);

			// The enriched broadcast may carry embedded sensors + populated
			// measurements. Prefer those when present, but never let a bare
			// (ref-only) payload clobber populated data already in the cache.
			const hasIncomingSensors = (eventData.sensors?.length ?? 0) > 0;
			const hasPopulatedIncomingMeasurements = eventData.measurements.some(
				(measurement) => typeof measurement === "object",
			);
			// Aggregator websocket payloads can arrive out of order relative to a
			// user-initiated status change. Only status_changed is authoritative
			// for status; measurement/summary updates keep the cached status.
			const applyStatusFromIncoming =
				"updateType" in eventDetail &&
				eventDetail.updateType === "status_changed";
			const websocketMergeOptions = {
				applyStatusFromIncoming,
				preferIncomingMeasurements: hasPopulatedIncomingMeasurements,
				preferIncomingSensors: hasIncomingSensors,
			};

			// Keep map marker "Latest Event" in sync even when the alert is
			// filtered out of the list (popup is sensor-scoped, not list-scoped).
			updateLatestEventBySensorCaches(
				queryClient,
				eventData,
				websocketMergeOptions,
			);

			// An open detail page should reflect an update whether or not the
			// event still belongs in the current list. Spread oldData first to
			// preserve populated relational fields (sensors, installation.location,
			// zones) that the websocket Event payload does not include in populated
			// form.
			const updateDetailQueries = () => {
				queryClient.setQueriesData<EventWithSensors>(
					{ queryKey: queryKeys.events.details() },
					(oldData) => {
						if (!oldData || typeof oldData !== "object" || !("id" in oldData)) {
							return oldData;
						}

						if ((oldData as Event).id !== eventId) {
							return oldData;
						}

						return mergeWebsocketEventIntoCachedEvent(
							oldData,
							eventData,
							websocketMergeOptions,
						);
					},
				);
			};

			// A status_changed → resolved drops the alert from open-only lists even
			// when the user is not on a filtered events page.
			const removeFromOpenOnlyLists =
				eventId != null &&
				shouldRemoveEventFromOpenOnlyLists({
					applyStatusFromIncoming,
					status: eventData.status,
				});

			if (removeFromOpenOnlyLists && eventId) {
				queryClient.setQueriesData<{
					pageParams: unknown[];
					pages: Array<{
						data: EventWithSensors[];
						endCursor?: string;
						hasMore?: boolean;
						total?: number;
					}>;
				}>(
					{
						exact: false,
						predicate: (query) => isOpenOnlyEventsListQueryKey(query.queryKey),
						queryKey: queryKeys.events.lists(),
					},
					(oldData) => removeEventFromInfiniteQueryData(oldData, eventId),
				);
				cachedWebSocketEventsRef.current.delete(eventId);
				// Resolved / down-ranked-out-of-open alerts must leave the
				// standing "N Alerts" map count, not only the open list.
				dismissMapEventFlash(eventId);
			}

			if (matchesFilters) {
				// Remove from filtered-out map if it was there
				if (eventId) {
					filteredOutEventsRef.current.delete(eventId);
				}

				if (websocketEventType === "event.created" && eventId) {
					// For new events, prepend to the first page of the cache for immediate UI update
					// This provides instant feedback while the server processes the event
					// Match all event list queries (infinite queries have "list" in their key)
					queryClient.setQueriesData<{
						pageParams: unknown[];
						pages: Array<{
							data: Event[];
							endCursor?: string;
							hasMore?: boolean;
							total?: number;
						}>;
					}>({ queryKey: queryKeys.events.lists() }, (oldData) => {
						// Only update infinite queries (they have pages array)
						if (
							!oldData ||
							typeof oldData !== "object" ||
							!("pages" in oldData) ||
							!Array.isArray(oldData.pages) ||
							oldData.pages.length === 0
						) {
							return oldData;
						}

						// Check if event already exists (avoid duplicates)
						const eventExists = oldData.pages.some((page) =>
							Array.isArray(page.data)
								? page.data.some((e) => e.id === eventId)
								: false,
						);

						if (eventExists) {
							return oldData;
						}

						// Prepend new event to the first page
						const firstPage = oldData.pages[0];
						if (!firstPage || !Array.isArray(firstPage.data)) {
							return oldData;
						}

						// Return new object to ensure React Query detects the change
						return {
							...oldData,
							pages: [
								{
									...firstPage,
									data: [eventData, ...firstPage.data],
									// Increment total if it exists
									total:
										firstPage.total !== undefined
											? firstPage.total + 1
											: undefined,
								},
								...oldData.pages.slice(1),
							],
						};
					});
				} else if (websocketEventType === "event.updated" && eventId) {
					// For updated events, update the cache directly for better performance
					// This updates all event queries (infinite queries and detail queries).
					// Open-only lists were already cleaned above when status left "open".

					queryClient.setQueriesData<{
						pageParams: unknown[];
						pages: Array<{
							data: EventWithSensors[];
							endCursor?: string;
							hasMore?: boolean;
							total?: number;
						}>;
					}>(
						{
							exact: false,
							predicate: (query) =>
								!removeFromOpenOnlyLists ||
								!isOpenOnlyEventsListQueryKey(query.queryKey),
							queryKey: queryKeys.events.lists(),
						},
						(oldData) => {
							// Only update infinite queries (they have pages array)
							if (
								!oldData ||
								typeof oldData !== "object" ||
								!("pages" in oldData) ||
								!Array.isArray(oldData.pages)
							) {
								return oldData;
							}

							// Merge websocket payload over cached EventWithSensors, preserving
							// populated relational fields that the bare websocket Event lacks.
							return {
								...oldData,
								pages: oldData.pages.map((page) => ({
									...page,
									data: Array.isArray(page.data)
										? page.data.map((existingEvent) => {
												if (existingEvent.id !== eventId) {
													return existingEvent;
												}
												return mergeWebsocketEventIntoCachedEvent(
													existingEvent,
													eventData,
													websocketMergeOptions,
												);
											})
										: page.data,
								})),
							};
						},
					);

					updateDetailQueries();
				}

				// Store/update the event in our cache tracker so we can preserve it when filters change.
				// Resolved alerts must not be re-injected into open-only Dashboard lists.
				if (eventId && !removeFromOpenOnlyLists) {
					cachedWebSocketEventsRef.current.set(eventId, eventData);

					// Prevent unbounded memory growth in long-lived sessions
					enforceMapSizeLimit(
						cachedWebSocketEventsRef.current,
						MAX_CACHED_EVENTS,
					);
					// List highlight: created + updated (main behavior). Map pulse /
					// "N Alerts" count: created only — measurement updates must not
					// re-pulse standing alerts for the rest of the session, but they
					// should refresh severity so a verified 4→9 does not keep the
					// low-priority color for the session.
					if (websocketEventType === "event.created") {
						addHighlightedEvent(
							eventId,
							extractMapEventFlashTarget(eventDetail),
						);
					} else {
						const nextSeverity = eventDetail.severity;
						if (typeof nextSeverity === "number") {
							setMapEventFlashes((prev) => {
								let changed = false;
								const next = prev.map((flash) => {
									if (flash.eventId !== eventId) return flash;
									if (flash.severity === nextSeverity) return flash;
									changed = true;
									return { ...flash, severity: nextSeverity };
								});
								return changed ? next : prev;
							});
						}
						addHighlightedEvent(eventId);
					}
				}
			} else {
				// Event does not match filters - store it for potential later inclusion.
				// Resolved alerts must not be re-injected into open-only Dashboard lists.
				if (eventId && !removeFromOpenOnlyLists) {
					filteredOutEventsRef.current.set(eventId, event);

					// Prevent unbounded memory growth in long-lived sessions
					enforceMapSizeLimit(filteredOutEventsRef.current, MAX_CACHED_EVENTS);
				}

				// An update that no longer matches the filters means the event was
				// (or may have been) visible and has since left this list — e.g. a
				// plausibility verification down-ranked a 9 to a 4 while the user
				// was looking at "moderate and above". Leaving the stale row in
				// place shows a severity the event no longer has, so drop it from
				// every list cache; the filtered-out map brings it back if the
				// filters widen. The detail page, if open, still gets the update.
				if (websocketEventType === "event.updated" && eventId) {
					cachedWebSocketEventsRef.current.delete(eventId);
					// Standing map counts belong to the dashboard map. An update
					// that only fails All Activity (or other page) filters must
					// not clear a still-open alert's dashboard flash.
					if (pathnameRef.current === ROUTES.DASHBOARD) {
						dismissMapEventFlash(eventId);
					}

					queryClient.setQueriesData<{
						pageParams: unknown[];
						pages: Array<{
							data: EventWithSensors[];
							endCursor?: string;
							hasMore?: boolean;
							total?: number;
						}>;
					}>(
						{ exact: false, queryKey: queryKeys.events.lists() },
						(oldData) => {
							if (
								!oldData ||
								typeof oldData !== "object" ||
								!("pages" in oldData) ||
								!Array.isArray(oldData.pages)
							) {
								return oldData;
							}

							let removed = 0;
							const pages = oldData.pages.map((page) => {
								if (!Array.isArray(page.data)) return page;
								const data = page.data.filter((existingEvent) => {
									if (existingEvent.id !== eventId) return true;
									removed += 1;
									return false;
								});
								return data.length === page.data.length
									? page
									: { ...page, data };
							});

							if (removed === 0) return oldData;

							// Keep the total honest on the first page, where it lives.
							const [firstPage, ...rest] = pages;
							return {
								...oldData,
								pages: firstPage
									? [
											{
												...firstPage,
												total:
													firstPage.total !== undefined
														? Math.max(0, firstPage.total - removed)
														: undefined,
											},
											...rest,
										]
									: pages,
							};
						},
					);

					updateDetailQueries();
				}
			}

			// Increment the counter only for new events that won't be visible in the UI.
			// Updates to existing events should never bump the badge.
			if (
				websocketEventType === "event.created" &&
				(!matchesUserFilters || !isOnEventsPage) &&
				isEventSeverityModerateOrHigher
			) {
				setNewEventsCount((prev) => prev + 1);
			}
		},
		[addHighlightedEvent, dismissMapEventFlash, queryClient],
	);

	// WebSocket connection - only connect if URL is provided
	const shouldConnect = Boolean(websocketUrl && websocketUrl.trim() !== "");
	const { disconnect, error, reconnect, status } = useWebSocketConnection({
		onEvent: shouldConnect ? handleEvent : undefined,
		websocketUrl: websocketUrl || "",
	});

	// Handle filter changes: wait for React Query to refetch with new filters,
	// preserve websocket events that match, and manage events that were filtered out.
	// Uses activeFilterProps to respect the current page's filters.
	//
	// Instead of calling invalidateQueries (which matches ALL cached queries by prefix,
	// causing cascading refetches), we remove stale inactive queries and passively wait
	// for the active query to refetch (React Query automatically refetches when the
	// query key changes due to filterParams changing in useInfiniteQuery).
	useEffect(() => {
		// Skip first execution — the initial query fetch handles mount
		if (!hasInitializedRef.current) {
			hasInitializedRef.current = true;
			return;
		}

		// Build the datetime span once; both loops match every tracked event against
		// the same filters/timezone, so rebuilding it per event is wasted work.
		const dateTimeRange = buildDateTimeRangeTimestamps(
			activeFilterProps.dateRangeFilter,
			timeFilterZone,
		);

		// Check if any previously filtered-out websocket events now match the new filters
		const eventsToAddFromFiltered: ParsedDashboardEvent[] = [];
		const eventsToRemoveFromFiltered: string[] = [];

		// Read the org-tree ID sets from the ref: a location-tree refetch must not
		// re-run this reinject (it clears inactive lists and prepends events). When
		// the tree gains IDs, the filters themselves change and re-run it.
		const idSets = idSetsRef.current;

		filteredOutEventsRef.current.forEach((event, eventId) => {
			const matchesFilters = doesEventMatchFilters(
				event.event.detail,
				activeFilterProps,
				timeFilterZone,
				dateTimeRange,
				idSets,
			);
			if (matchesFilters) {
				eventsToAddFromFiltered.push(event);
				eventsToRemoveFromFiltered.push(eventId);
			}
		});

		// Do NOT delete from filteredOutEventsRef yet. `resetFilters` dispatches
		// twice (reset → hydrate L/Z/S defaults), so this effect runs, starts the
		// async reinject, then is cancelled by the hydrate update. Deleting here
		// permanently drops those events before they can be prepended.

		// Filter cached websocket events to only keep those that match current filters
		const eventsToPreserve: Event[] = [];
		const eventsToRemoveFromCache: string[] = [];

		cachedWebSocketEventsRef.current.forEach((event, eventId) => {
			const matchesFilters = doesEventMatchFiltersFromCache(
				event,
				activeFilterProps,
				timeFilterZone,
				dateTimeRange,
				idSets,
			);
			if (matchesFilters) {
				eventsToPreserve.push(event);
			} else {
				eventsToRemoveFromCache.push(eventId);
			}
		});

		// Combine events to add: previously filtered-out events + cached events to preserve
		const allEventsToAdd = [
			...eventsToAddFromFiltered.map((event) =>
				convertWebSocketEventToEvent(event.event.detail),
			),
			...eventsToPreserve,
		];

		// Nothing to reinject or prune — skip async work (avoids touching the live list).
		if (
			allEventsToAdd.length === 0 &&
			eventsToRemoveFromCache.length === 0 &&
			eventsToRemoveFromFiltered.length === 0
		) {
			return;
		}

		let cancelled = false;

		void (async () => {
			// Remove old cached queries with previous filter params so they can't
			// be matched by subsequent setQueriesData calls
			queryClient.removeQueries({
				queryKey: queryKeys.events.lists(),
				type: "inactive",
			});

			// Wait for React Query's automatic refetch to settle (triggered by
			// the query key change in useInfiniteQuery).
			await waitForListQueriesToSettle(queryClient, queryKeys.events.lists());

			if (cancelled) return;

			// Re-read the trackers after the wait: an event.updated may have resolved
			// an event (removed from the ref) or replaced it with a newer copy.
			const currentEventsToAdd = [
				...eventsToRemoveFromFiltered.flatMap((eventId) => {
					const pending = filteredOutEventsRef.current.get(eventId);
					return pending
						? [convertWebSocketEventToEvent(pending.event.detail)]
						: [];
				}),
				...eventsToPreserve.flatMap((event) => {
					const current = cachedWebSocketEventsRef.current.get(event.id);
					return current ? [current] : [];
				}),
			].filter((event) =>
				doesEventMatchFiltersFromCache(
					event,
					activeFilterProps,
					timeFilterZone,
					dateTimeRange,
					idSets,
				),
			);

			// Re-inject websocket events that match current filters after refetch completes
			if (currentEventsToAdd.length > 0) {
				queryClient.setQueriesData<{
					pageParams: unknown[];
					pages: Array<{
						data: Event[];
						endCursor?: string;
						hasMore?: boolean;
						total?: number;
					}>;
				}>({ queryKey: queryKeys.events.lists() }, (oldData) => {
					// Only process infinite queries (they have pages array)
					if (
						!oldData ||
						typeof oldData !== "object" ||
						!("pages" in oldData) ||
						!Array.isArray(oldData.pages) ||
						oldData.pages.length === 0
					) {
						return oldData;
					}

					const firstPage = oldData.pages[0];
					if (!firstPage || !Array.isArray(firstPage.data)) {
						return oldData;
					}

					// Add websocket events that match current filters. Check every
					// loaded page, not just the first, so an event already further
					// down the list is not prepended a second time.
					const existingIds = new Set(
						oldData.pages.flatMap((page) =>
							Array.isArray(page.data) ? page.data.map((e) => e.id) : [],
						),
					);
					const eventsToAddToPage = currentEventsToAdd.filter(
						(eventData) => !existingIds.has(eventData.id),
					);

					if (eventsToAddToPage.length === 0) {
						return oldData;
					}

					// Return new object with websocket events prepended
					return {
						...oldData,
						pages: [
							{
								...firstPage,
								data: [...eventsToAddToPage, ...firstPage.data],
								total:
									firstPage.total !== undefined
										? firstPage.total + eventsToAddToPage.length
										: undefined,
							},
							...oldData.pages.slice(1),
						],
					};
				});

				// Track reinjected filtered-out events so later filter changes can
				// prune them. Preserved events are already tracked with their latest copy.
				for (const eventData of currentEventsToAdd) {
					if (!cachedWebSocketEventsRef.current.has(eventData.id)) {
						cachedWebSocketEventsRef.current.set(eventData.id, eventData);
					}
				}
			}

			// Only clear trackers for ids still pending — overlapping filter-change
			// effects must not double-decrement the badge.
			const reinjectedFilteredOutIds = eventsToRemoveFromFiltered.filter(
				(eventId) => filteredOutEventsRef.current.has(eventId),
			);
			reinjectedFilteredOutIds.forEach((eventId) => {
				filteredOutEventsRef.current.delete(eventId);
			});
			eventsToRemoveFromCache.forEach((eventId) => {
				cachedWebSocketEventsRef.current.delete(eventId);
			});

			if (reinjectedFilteredOutIds.length > 0) {
				setNewEventsCount((prev) =>
					Math.max(0, prev - reinjectedFilteredOutIds.length),
				);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [activeFilterProps, queryClient, timeFilterZone]);

	// Cleanup highlight timeouts on unmount
	useEffect(() => {
		// Capture current ref value for cleanup
		const timeoutsMap = highlightTimeoutsRef.current;

		return () => {
			timeoutsMap.forEach((timeouts) => {
				clearTimeout(timeouts.list);
				if (timeouts.map) clearTimeout(timeouts.map);
			});
			timeoutsMap.clear();
		};
	}, []);

	// Memoize context value to prevent unnecessary re-renders
	const contextValue = useMemo<WebSocketContextValue>(() => {
		// If no WebSocket URL is provided, return a no-op context value
		if (!websocketUrl || websocketUrl.trim() === "") {
			return {
				clearNewEventsCount: () => {},
				disconnect: () => {},
				dismissMapEventFlash: () => {},
				error: null,
				highlightedEventIds: new Set(),
				mapEventFlashes: [],
				newEventsCount: 0,
				reconnect: () => {},
				status: "disconnected" as const,
			};
		}

		return {
			clearNewEventsCount,
			disconnect,
			dismissMapEventFlash,
			error,
			highlightedEventIds,
			mapEventFlashes,
			newEventsCount,
			reconnect,
			status,
		};
	}, [
		clearNewEventsCount,
		disconnect,
		dismissMapEventFlash,
		error,
		highlightedEventIds,
		mapEventFlashes,
		newEventsCount,
		reconnect,
		status,
		websocketUrl,
	]);

	return (
		<WebSocketContext.Provider value={contextValue}>
			{children}
		</WebSocketContext.Provider>
	);
}

/**
 * Extract the event ID from a parsed dashboard event
 */
function getEventIdFromParsedEvent(
	event: ParsedDashboardEvent,
): string | undefined {
	if (event.type === "event.created" || event.type === "event.updated") {
		return event.event.detail.id;
	}
	return undefined;
}

/**
 * Extract the location name from a WebSocket event detail.
 * The event may include a populated installation object with a populated location.
 * Uses Record checks because the WS payload may include fields not in the base type.
 */
function getLocationNameFromDetail(
	eventDetail: WebSocketEventDetail,
): string | undefined {
	const detail = eventDetail as unknown as Record<string, unknown>;
	const installation = detail.installation;
	if (!installation || typeof installation === "string") return undefined;
	const inst = installation as Record<string, unknown>;
	const location = inst.location;
	if (!location || typeof location === "string") return undefined;
	const loc = location as Record<string, unknown>;
	return typeof loc.name === "string" ? loc.name : undefined;
}

// Re-export types for convenience
export type {
	ConnectionStatus,
	DashboardWebSocketEvent,
	DashboardWebSocketEventType,
	EventCreatedWebSocketEvent,
	EventUpdatedWebSocketEvent,
	ParsedDashboardEvent,
} from "@/hooks/useWebSocketConnection";
